import { Link, useNavigate } from "react-router-dom";
import PublicHeader from "@/components/PublicHeader";
import { contactMailto, CONTACT_EMAIL } from "@/lib/contact";
import { ArrowLeft } from "@phosphor-icons/react";

const EFFECTIVE_DATE = "July 13, 2026";

function Section({ n, title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-serif-display text-2xl sm:text-3xl font-bold tracking-tight text-[#0D1B2A] mb-3">
        <span className="text-[#B47A3C]">{n}.</span> {title}
      </h2>
      <div className="space-y-3 text-[15px] sm:text-base text-[#3A3730] leading-relaxed">{children}</div>
    </section>
  );
}

export default function TermsPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="terms-page">
      <PublicHeader />
      <main className="max-w-3xl mx-auto px-6 py-14">
        <button onClick={() => navigate(-1)} data-testid="terms-back" className="inline-flex items-center gap-2 font-medium text-[#525252] hover:text-[#B47A3C] transition-colors mb-8">
          <ArrowLeft size={16} weight="bold" /> Back
        </button>
        <p className="label-caps text-[#B47A3C] mb-2">The fine print, in plain English</p>
        <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-none font-medium mb-3">Terms of Service &amp; Privacy Policy</h1>
        <p className="text-[15px] text-[#8A867D] mb-12">Effective {EFFECTIVE_DATE} · Questions? <a href={contactMailto("Vintage Vantage — Terms question")} className="text-[#B47A3C] underline underline-offset-4">{CONTACT_EMAIL}</a></p>

        <Section n={1} title="What Vintage Vantage is (and isn't)">
          <p>
            Vintage Vantage ("we," "us," "the Service") is a listing, organization and decision-support tool
            for vintage and high-end resellers. We help you build a seller-owned inventory, draft listings,
            and prepare cross-list exports for marketplaces you choose.
          </p>
          <p>
            We are <strong>not a marketplace</strong>. We don't guarantee marketplace approval (curated platforms
            like 1stDibs and Chairish decide who gets in), we don't handle shipping or payments between you and
            your buyers, and we don't promise a sale. Marketplace policies, commissions and fees change —
            always verify current terms on each marketplace before listing.
          </p>
        </Section>

        <Section n={2} title="Your account">
          <p>
            You must provide accurate information when creating an account and keep your credentials secure.
            You're responsible for activity under your account. You must be at least 18 to use the Service.
            We may suspend accounts that abuse the Service, harm other members, or violate these terms.
          </p>
        </Section>

        <Section n={3} title="Your inventory is yours">
          <p>
            The listings, photos, descriptions, measurements and knowledge you create or recover through
            Vintage Vantage <strong>belong to you</strong>. You can export your inventory at any time — even if
            you cancel. We claim no ownership over your content; you grant us only the limited license needed
            to store, process and display it so the Service can work for you.
          </p>
        </Section>

        <Section n={4} title="AI-assisted features">
          <p>
            Features like Fantastic Finds, AI descriptions, and pricing guidance are <strong>decision support,
            not decisions</strong>. AI can be wrong — identifications, era estimates, maker attributions and price
            suggestions are informed starting points that you must verify. Final approval of every listing is
            always yours, and you're responsible for the accuracy of what you publish on any marketplace.
          </p>
        </Section>

        <Section n={5} title="Plans, billing & the Founding Council">
          <p>
            Paid plans are billed through Stripe. Subscriptions renew automatically until cancelled; you can
            cancel anytime and keep access through the end of your paid period. Cross-list credits and monthly
            allowances reset per your plan's terms and don't carry over unless stated. The Founding Council
            lifetime rate stays locked as long as your subscription remains active and in good standing.
            Trial terms (duration, included cross-lists) are shown at signup.
          </p>
        </Section>

        <Section n={6} title="Community & feedback">
          <p>
            Be decent in the Seller Community — no spam, harassment, or posting content you don't have the
            right to share. We may remove content that breaks these rules. Feedback and suggestions you send
            us (including "How are we doing?" notes) may be used to improve the Service; anything we build
            remains ours, and your inventory remains yours.
          </p>
        </Section>

        <Section n={7} title="Privacy — what we collect and why">
          <p>We collect only what the Service needs:</p>
          <p>
            <strong>Account data</strong> (name, email, password hash or Google sign-in) to run your account.{" "}
            <strong>Inventory data</strong> (listings, photos, prices) to provide the core Service.{" "}
            <strong>Usage &amp; feedback</strong> (mood notes, the page they came from, community posts) to
            improve the product. <strong>Payment data</strong> is handled by Stripe — we never see or store
            your full card number.
          </p>
          <p>
            <strong>We never sell your data.</strong> Community statistics shown publicly are opt-in, anonymized
            and aggregated — individual listings are never shown, and you can switch sharing off anytime.
          </p>
          <p>
            Trusted processors we use to operate: Stripe (payments), AI providers (listing enrichment — your
            photos/text are processed to generate drafts), Resend (email), and cloud hosting/storage. Each
            receives only what's needed for its job.
          </p>
          <p>
            Cookies: we use a session cookie to keep you signed in — no advertising trackers. To export or
            delete your data, email <a href={contactMailto("Vintage Vantage — data request")} className="text-[#B47A3C] underline underline-offset-4">{CONTACT_EMAIL}</a> and
            we'll handle it promptly.
          </p>
        </Section>

        <Section n={8} title="Disclaimers & limitation of liability">
          <p>
            The Service is provided "as is." To the fullest extent permitted by law, we disclaim warranties of
            merchantability and fitness for a particular purpose, and we're not liable for indirect, incidental
            or consequential damages — including lost profits, lost sales, or marketplace decisions. Our total
            liability for any claim is limited to the amount you paid us in the 12 months before the claim.
          </p>
        </Section>

        <Section n={9} title="Changes & contact">
          <p>
            We may update these terms as the Service evolves; material changes will be announced in the app.
            Continued use after changes means acceptance. Questions, issues, or just want to talk shop?{" "}
            <a href={contactMailto("Vintage Vantage — hello")} className="text-[#B47A3C] underline underline-offset-4">{CONTACT_EMAIL}</a>.
          </p>
        </Section>

        <div className="border-l-2 border-[#B47A3C] pl-4 mb-12">
          <p className="font-serif-display text-xl italic text-[#0D1B2A]">
            The short version: your work belongs to you, the AI assists but you decide, and we never over-promise.
          </p>
        </div>

        <Link to="/register" data-testid="terms-register-cta" className="inline-flex items-center h-12 px-6 bg-[#0D1B2A] text-[#FDFBF7] rounded-none font-medium hover:bg-black transition-colors">
          Sounds fair — get started
        </Link>
      </main>
    </div>
  );
}
